import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  companyLogo: null,
  content: { winWire: [], insights: [], reports: [] },
  notifications: { winWire: 0, insights: 0, reports: 0 },
  loading: false,
  notificationsLoading: false,
  tagUserList: [],
};

const hub = createSlice({
  name: "hub",
  initialState,
  reducers: {
    getHubSaga(state) {
      state.loading = true;
    },
    getHubContent(state, action) {
      const { companyLogo, content, userList } = action.payload;
      state.content = content;
      state.companyLogo = companyLogo;
      state.loading = false;
      state.tagUserList = userList;
    },
    getHubContentFail(state) {
      state.loading = false;
    },
    getWinWireMessagesSaga(state) {
      state.loading = true;
    },
    getWinWireMessages(state, action) {
      state.content = {
        ...state.content,
        winWire: [...action.payload.winWireMessages, ...state.content.winWire],
      };
      state.loading = false;
    },
    getWinWireMessagesFail(state) {
      state.loading = false;
    },
    sendWinWireReplySaga(state) {
      state.loading = true;
    },
    updateWinWireReplySaga(state) {
      state.loading = true;
    },
    deleteWinWireReplySaga(state) {
      state.loading = true;
    },
    addWinWireReply(state, action) {
      // Incoming from websocket
      const message = state.content.winWire.find(
        (msg) => msg.id === action.payload.FK_win_wire_messages_id
      );

      if (message) {
        if (!message.WinWireReplies) {
          message.WinWireReplies = [];
        }
        const insertIndex = message.WinWireReplies.findIndex(
          (reply) => new Date(reply.createdAt) > new Date(action.payload.createdAt)
        );

        if (insertIndex === -1) {
          message.WinWireReplies.push(action.payload);
        } else {
          message.WinWireReplies.splice(insertIndex, 0, action.payload);
        }
      }
    },
    editWinWireReply(state, action) {
      // Incoming from websocket
      const { FK_win_wire_messages_id: messageId, id: replyId } = action.payload;

      const message = state.content.winWire.find((msg) => msg.id === messageId);
      if (message && message.WinWireReplies) {
        message.WinWireReplies = message.WinWireReplies.map((r) =>
          r.id === replyId ? action.payload : r
        );
      }
    },
    removeWinWireReply(state, action) {
      // Incoming from websocket
      const { messageId, replyId } = action.payload;

      const message = state.content.winWire.find((msg) => msg.id === +messageId);
      if (message && message.WinWireReplies) {
        message.WinWireReplies = message.WinWireReplies.filter((r) => r.id !== +replyId);
      }
    },
    addWinWireReaction(state, action) {
      // Incoming from websocket
      const { FK_win_wire_messages_id: messageId, FK_win_wire_replies_id: replyId } =
        action.payload;
      const message = state.content.winWire.find((msg) => msg.id === messageId);
      if (!message) return;

      // determine reaction target
      const target =
        replyId === null ? message : message.WinWireReplies.find((reply) => reply.id === replyId);

      if (!target) return;
      if (!target.WinWireReactions) {
        target.WinWireReactions = [];
      }
      target.WinWireReactions.push(action.payload);
    },
    removeWinWireReaction: (state, action) => {
      // Incoming from websocket
      const { messageId, reaction, uclId, replyId } = action.payload;
      const message = state.content.winWire.find((msg) => msg.id === +messageId);
      if (!message) return;

      // determine reaction target
      const target =
        replyId === null || replyId === undefined
          ? message
          : message.WinWireReplies.find((reply) => reply.id === +replyId);

      if (!target?.WinWireReactions) return;
      target.WinWireReactions = target.WinWireReactions.filter(
        (r) => !(r.reaction === reaction && r.FK_user_companies_links_id === uclId)
      );
    },
    addWinWireMessage(state, action) {
      // Incoming from websocket
      const newMessage = action.payload;
      const { content } = state;
      const winWireMessages = [...content.winWire];

      const insertIndex = winWireMessages.findIndex(
        (message) => new Date(newMessage.createdAt) < new Date(message.createdAt)
      );

      if (insertIndex === -1) {
        winWireMessages.push(newMessage);
      } else {
        winWireMessages.splice(insertIndex, 0, newMessage);
      }

      const { hash } = window.location;
      if (hash !== "#win-wire") {
        // Add notification
        const { winWire } = state.notifications;
        state.notifications = { ...state.notifications, winWire: winWire + 1 };
      }

      state.content = { ...content, winWire: winWireMessages };
    },
    removeWinWireMessages(state, action) {
      // Incoming from websocket
      const removedMessageIds = action.payload;
      const { content } = state;

      const winWireMessages = [...content.winWire].filter(
        (message) => !removedMessageIds.includes(message.id)
      );

      state.content = { ...content, winWire: winWireMessages };
    },
    addReaction: (state) => {
      state.loading = true;
    },
    removeReaction: (state) => {
      state.loading = true;
    },
    handleInsightMute: (state) => {
      state.loading = true;
    },
    winWireFinished(state) {
      state.loading = false;
    },
    fetchNotificationsSaga(state) {
      state.notificationsLoading = true;
    },
    setNotifications(state, action) {
      // Account for scenario when user initially loads on one of the hub hashes
      const { hash } = window.location;
      if (hash === "#win-wire") {
        state.notifications = { ...action.payload, winWire: 0 };
      } else if (hash === "#insights") {
        state.notifications = { ...action.payload, insights: 0 };
      } else if (hash === "#reports") {
        state.notifications = { ...action.payload, reports: 0 };
      } else {
        state.notifications = action.payload;
      }
    },
    fetchNotificationsFinished(state) {
      state.notificationsLoading = false;
    },
    removeNotifications(state, action) {
      const { key } = action.payload;
      if (key === "winWire") {
        state.notifications = { ...state.notifications, winWire: 0 };
      } else if (key === "insights") {
        state.notifications = { ...state.notifications, insights: 0 };
      } else if (key === "reports") {
        state.notifications = { ...state.notifications, reports: 0 };
      }
    },
  },
});
export const hubAction = hub.actions;
export default hub.reducer;
